import axios, {
  AxiosError,
  AxiosHeaders,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { getAuthSnapshot, useAuthStore } from '@/stores/auth';
import type { ApiError } from './types';

/**
 * Base URL for all dashboard API calls. The Vite dev server proxies `/api/*`
 * to the Axum backend (see `vite.config.ts`); in production the same origin
 * serves the SPA and the API.
 */
export const API_BASE_URL = '/api/v1';

/** Default request timeout in milliseconds. */
export const DEFAULT_TIMEOUT_MS = 30_000;

/** Path used by the auth refresh round-trip. Skipped by request interceptor. */
const REFRESH_PATH = '/auth/refresh';

/**
 * Login route to redirect to when a session can no longer be recovered. The
 * router is mounted under {@link import.meta.env.BASE_URL} so we prefix it
 * here to avoid landing the user on a non-existent URL when the dashboard is
 * served at a sub-path (e.g. `/dashboard/`).
 */
const LOGIN_PATH = `${(import.meta.env.BASE_URL ?? '/').replace(/\/$/, '')}/auth/login`;

interface RetriableConfig extends InternalAxiosRequestConfig {
  /** Set after we have already attempted a refresh on this request. */
  _retry?: boolean;
  /** Set on the refresh request itself so we never recurse. */
  _isRefresh?: boolean;
}

/**
 * Axios instance pre-configured for the dashboard. Prefer the typed helpers
 * (`api.get`, `api.post`, etc.) exported below — they unwrap the response
 * payload directly so call sites don't have to read `.data` manually.
 */
export const httpClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: DEFAULT_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// ---------- Request interceptor: inject Authorization header ---------------

httpClient.interceptors.request.use((config) => {
  const { accessToken } = getAuthSnapshot();
  if (accessToken) {
    const headers =
      config.headers instanceof AxiosHeaders
        ? config.headers
        : new AxiosHeaders(config.headers);
    headers.set('Authorization', `Bearer ${accessToken}`);
    config.headers = headers;
  }
  return config;
});

// ---------- Response interceptor: refresh-on-401, redirect on failure -------

/**
 * Single in-flight refresh promise, shared by every request that hits 401
 * concurrently. Resolves to the new access token on success, `null` on
 * failure.
 */
let refreshInFlight: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
  const { refreshToken } = getAuthSnapshot();
  if (!refreshToken) return null;

  try {
    const response = await httpClient.post<{
      access_token: string;
      refresh_token?: string;
    }>(
      REFRESH_PATH,
      { refresh_token: refreshToken },
      { _isRefresh: true } as RetriableConfig,
    );

    const accessToken = response.data?.access_token;
    if (!accessToken) return null;

    const store = useAuthStore.getState();
    store.setSession({
      accessToken,
      refreshToken: response.data.refresh_token ?? refreshToken,
      user: store.user,
    });
    return accessToken;
  } catch {
    return null;
  }
}

function redirectToLogin(): void {
  if (typeof window === 'undefined') return;
  // Avoid a redirect loop if we're already on the login page.
  if (window.location.pathname.startsWith(LOGIN_PATH)) return;
  window.location.assign(LOGIN_PATH);
}

httpClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const original = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    // No config (network error before request was built) or unrelated status — passthrough.
    if (!original || status !== 401) {
      return Promise.reject(error);
    }

    // The refresh request itself failed: clear the session and bail out.
    if (original._isRefresh) {
      useAuthStore.getState().clearSession();
      redirectToLogin();
      return Promise.reject(error);
    }

    // We have already retried once for this request; don't loop.
    if (original._retry) {
      useAuthStore.getState().clearSession();
      redirectToLogin();
      return Promise.reject(error);
    }
    original._retry = true;

    // Coalesce concurrent refreshes into a single network call.
    refreshInFlight ??= performRefresh().finally(() => {
      refreshInFlight = null;
    });

    const newToken = await refreshInFlight;
    if (!newToken) {
      useAuthStore.getState().clearSession();
      redirectToLogin();
      return Promise.reject(error);
    }

    // Replay the original request with the new token.
    const headers =
      original.headers instanceof AxiosHeaders
        ? original.headers
        : new AxiosHeaders(original.headers);
    headers.set('Authorization', `Bearer ${newToken}`);
    original.headers = headers;
    return httpClient.request(original);
  },
);

// ---------- Typed convenience helpers --------------------------------------

function unwrap<T>(p: Promise<AxiosResponse<T>>): Promise<T> {
  return p.then((r) => r.data);
}

/**
 * Typed convenience facade over the Axios instance. Each method returns the
 * unwrapped response body so call sites stay terse:
 *
 * ```ts
 * const me = await api.get<AuthUser>('/users/me');
 * ```
 */
export const api = {
  get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return unwrap(httpClient.get<T>(url, config));
  },
  post<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return unwrap(httpClient.post<T>(url, body, config));
  },
  put<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return unwrap(httpClient.put<T>(url, body, config));
  },
  patch<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return unwrap(httpClient.patch<T>(url, body, config));
  },
  delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return unwrap(httpClient.delete<T>(url, config));
  },
  /** Escape hatch when callers need the full Axios response (headers etc.). */
  raw: httpClient,
};

export type Api = typeof api;
